import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, Check, AlertCircle, XCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { verifyResetToken, resetPassword } = useAuth();

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      const result = await verifyResetToken(token);
      setTokenValid(result.success);
      if (result.success) {
        setTokenEmail(result.email);
      }
      setVerifying(false);
    };

    verifyToken();
  }, [token, verifyResetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const result = await resetPassword(token, password);
    
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Failed to reset password. Please try again.');
    }
    
    setLoading(false);
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Verifying reset link...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-8">
          {/* Invalid Token */}
          {!tokenValid && !success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Invalid or Expired Link</h1>
              <p className="text-gray-400 mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] text-white"
              >
                Back to Home
              </Button>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-2">Password Reset Successfully</h1>
              <p className="text-gray-400 mb-6">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] text-white"
              >
                Sign In
              </Button>
            </div>
          )}

          {/* Reset Form */}
          {tokenValid && !success && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#3B82F6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-[#3B82F6]" />
                </div>
                <h1 className="text-xl font-bold text-white mb-2">Create New Password</h1>
                <p className="text-gray-400 text-sm">
                  Reset password for <span className="text-white">{tokenEmail}</span>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password (min 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pl-10 pr-10 bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pl-10 bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="confirm-new-password"
                  />
                </div>

                {/* Password Requirements */}
                <div className="bg-[#1e2a45]/50 rounded-lg p-3">
                  <p className="text-xs text-gray-400 mb-2">Password requirements:</p>
                  <ul className="text-xs space-y-1">
                    <li className={`flex items-center gap-2 ${password.length >= 8 ? 'text-green-400' : 'text-gray-500'}`}>
                      <Check className={`h-3 w-3 ${password.length >= 8 ? '' : 'opacity-30'}`} />
                      At least 8 characters
                    </li>
                    <li className={`flex items-center gap-2 ${password === confirmPassword && password.length > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                      <Check className={`h-3 w-3 ${password === confirmPassword && password.length > 0 ? '' : 'opacity-30'}`} />
                      Passwords match
                    </li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  disabled={loading || password.length < 8 || password !== confirmPassword}
                  className="w-full bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white font-semibold py-2.5"
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
